"use client";

import { CSSProperties } from "react";
import NextImage from "next/image";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { stegaClean } from "@sanity/client/stega";

import { cn } from "@/lib/utils";
import { useCountdown } from "@/lib/utilHooks";
import { GROQButton, GROQCTA } from "@/sanity/lib/definitions";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { DEFAULT_LANGUAGE, NON_DEFAULT_LANGUAGES, resolveI18nText } from "@/sanity/globals/I18n";

import gridTile from "@/public/event/grid-tile.png";
import wave from "@/public/event/wave.svg";
import candle1 from "@/public/event/candle-1.svg";
import candle2 from "@/public/event/candle-2.svg";
import candle3 from "@/public/event/candle-3.svg";
import candle4 from "@/public/event/candle-4.svg";
import wordmark from "@/public/event/wordmark.svg";
import logoMark from "@/public/event/logo-mark.svg";

export { gridTile, wave, candle1, candle2, candle3, candle4, wordmark, logoMark };

/**
 * Figma "vertical trim: cap height to baseline" on a text node. Makes the browser
 * line box match the Figma box so auto-layout gaps transfer 1:1 (Chrome 133+ /
 * Safari 18.2+; other browsers keep the regular line box).
 */
export const CAP_TRIM = "[text-box-trim:trim-both] [text-box-edge:cap_alphabetic]";

export type EventLayout = "event" | "monthly";

export type TicketGeometry = {
  /** Circle notch radius / how far its centre sits outside the edge. */
  notchR: string;
  notchOffset: string;
  /** Outer solid band + see-through inner outline (omit for plain pieces). */
  outline?: { band: string; line: string };
  /** Perforation dashes on the stub's tear edge. */
  dashes: { width: string; length: string; period: string; top: string; height: string };
};

export function ticketPieceMask(edges: Array<"left" | "right">, g: TicketGeometry): CSSProperties {
  const tearL = edges.includes("left");
  const tearR = edges.includes("right");
  const notch = (x: string, cy: string) =>
    `radial-gradient(circle at ${x} ${cy}, #000 calc(${g.notchR} - 0.5px), transparent calc(${g.notchR} + 0.5px))`;
  const solid = "linear-gradient(#000, #000)";

  const images = [solid];
  const sizes = ["100% 100%"];
  const positions = ["0 0"];

  for (const edge of edges) {
    const x = edge === "right" ? "100%" : "0%";
    images.push(notch(x, `-${g.notchOffset}`), notch(x, `calc(100% + ${g.notchOffset})`));
    sizes.push("100% 100%", "100% 100%");
    positions.push("0 0", "0 0");
  }

  if (g.outline) {
    const { band, line } = g.outline;
    // A torn edge's outline runs into the notch cut (~0.66r wide at the outline's depth);
    // a straight edge keeps the outer band.
    const cut = `${g.notchR} * 0.66`;
    const leftInset = tearL ? `(${cut})` : band;
    const rightInset = tearR ? `(${cut})` : band;
    const lineLen = `calc(100% - ${leftInset} - ${rightInset})`;
    images.push(solid, solid);
    sizes.push(`${lineLen} ${line}`, `${lineLen} ${line}`);
    positions.push(`left calc(${leftInset}) top ${band}`, `left calc(${leftInset}) bottom ${band}`);
    if (!tearL) {
      images.push(solid);
      sizes.push(`${line} calc(100% - ${band} * 2)`);
      positions.push(`left ${band} top ${band}`);
    }
    if (!tearR) {
      images.push(solid);
      sizes.push(`${line} calc(100% - ${band} * 2)`);
      positions.push(`right ${band} top ${band}`);
    }
  }
  if (tearL) {
    const d = g.dashes;
    images.push(`repeating-linear-gradient(to bottom, #000 0 ${d.length}, transparent ${d.length} ${d.period})`);
    sizes.push(`${d.width} ${d.height}`);
    positions.push(`left 0 top ${d.top}`);
  }

  return {
    maskImage: images.join(", "),
    maskSize: sizes.join(", "),
    maskPosition: positions.join(", "),
    maskRepeat: "no-repeat",
    maskComposite: images.map((_, i) => (i === 0 ? "subtract" : "add")).join(", "),
  };
}

/** Hero/why-attend CTA: 381×61 in Figma, 1rem = 8px. */
export const CTA_GEOMETRY: TicketGeometry = {
  notchR: "1.0625rem",
  notchOffset: "0.27rem",
  outline: { band: "0.375rem", line: "0.125rem" },
  dashes: { width: "0.125rem", length: "0.265rem", period: "0.53rem", top: "1.19rem", height: "5.5625rem" },
};
export const CTA_BODY_MASK = ticketPieceMask(["right"], CTA_GEOMETRY);
export const CTA_STUB_MASK = ticketPieceMask(["left"], CTA_GEOMETRY);

/** Navbar button: 250×45 in Figma, plain white, no band. */
export const NAV_GEOMETRY: TicketGeometry = {
  notchR: "1rem",
  notchOffset: "0.466rem",
  dashes: { width: "0.1rem", length: "0.197rem", period: "0.394rem", top: "0.83rem", height: "4.13rem" },
};
export const NAV_BODY_MASK = ticketPieceMask(["right"], NAV_GEOMETRY);
export const NAV_STUB_MASK = ticketPieceMask(["left"], NAV_GEOMETRY);

/** Language from the URL prefix (`/de/...`), same resolution as `CTAButton`. */
function useUrlLanguage() {
  const pathname = usePathname();
  return (
    pathname
      .split("/")
      .filter(Boolean)
      .find((segment) => NON_DEFAULT_LANGUAGES.includes(segment)) ?? DEFAULT_LANGUAGE
  );
}

/** Countdown unit labels (days, hours, minutes, seconds) per language. */
const COUNTDOWN_UNIT_LABELS: Record<string, [string, string, string, string]> = {
  en: ["DAYS", "HOURS", "MINUTES", "SECONDS"],
  de: ["TAGE", "STUNDEN", "MINUTEN", "SEKUNDEN"],
  br: ["DIAS", "HORAS", "MINUTOS", "SEGUNDOS"],
};

/** "Ends in" label of the compact navbar countdown, per language. */
const NAV_COUNTDOWN_LABELS: Record<string, string> = {
  en: "ENDS IN",
  de: "ENDET IN",
  br: "TERMINA EM",
};

/** Countdown units in the page language, falling back to the URL. No target shows `00`. */
export function useCountdownUnits(target?: string, pageLanguage?: string) {
  const { days, hours, minutes, seconds } = useCountdown(target ?? null);
  const urlLanguage = useUrlLanguage();
  const language = stegaClean(pageLanguage) || urlLanguage;
  const unitLabels = COUNTDOWN_UNIT_LABELS[language] ?? COUNTDOWN_UNIT_LABELS[DEFAULT_LANGUAGE];
  const values = target ? [days, hours, minutes, seconds] : ["00", "00", "00", "00"];
  return values.map((value, i) => ({ value, label: unitLabels[i] }));
}

/** Compact `00:00:00:00` boxes (mobile navbar row, replay hero). */
export function CompactCountdown({
  target,
  variant = "dark",
  className,
}: {
  target?: string;
  variant?: "dark" | "light";
  className?: string;
}) {
  const units = useCountdownUnits(target);
  const dark = variant === "dark";
  return (
    <div className={cn("flex items-start gap-[0.25rem]", className)}>
      {units.map((unit, i) => (
        <div
          key={unit.label}
          className="contents">
          {i > 0 && (
            <p
              className={cn(
                "mb-0 font-sans text-[2.5rem] leading-[3.75rem] font-bold tracking-[-0.075rem]",
                dark ? "text-white" : "text-black",
              )}>
              :
            </p>
          )}
          <span
            className={cn(
              "flex items-center justify-center rounded-[0.75rem] border px-[0.625rem] py-[0.25rem] backdrop-blur-[1.25rem]",
              dark ? "border-white/10 bg-white/10" : "border-[#1b8869]/20 bg-[#124335]/[0.06]",
            )}>
            <span
              className={cn(
                "font-sans text-[2.5rem] leading-[3.75rem] font-semibold tracking-[-0.075rem]",
                dark ? "text-white" : "text-black",
              )}>
              {unit.value}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** Compact `ENDS IN 00:00:00:00` row under the mobile navbar (Figma 6876:3469). */
function NavCountdown({ target, language: pageLanguage }: { target?: string; language?: string }) {
  const urlLanguage = useUrlLanguage();
  const language = stegaClean(pageLanguage) || urlLanguage;
  const label = NAV_COUNTDOWN_LABELS[language] ?? NAV_COUNTDOWN_LABELS[DEFAULT_LANGUAGE];

  return (
    <div className="flex items-center justify-center gap-[1.25rem]">
      {/* <p className="mb-0 font-geist text-[1.75rem] leading-[2rem] font-medium whitespace-nowrap text-white opacity-50"> */}
      {/*   {label} */}
      {/* </p> */}
      <CompactCountdown target={target} />
    </div>
  );
}

export function CountdownBox({
  label,
  target,
  language,
  variant = "dark",
  className,
  layout = "event",
  fitWidth = false,
}: {
  label?: string;
  target?: string;
  language?: string;
  variant?: "dark" | "light";
  className?: string;
  fitWidth?: boolean;
  layout?: EventLayout | "thankyou";
}) {
  const units = useCountdownUnits(target, language);
  const monthly = layout === "monthly";
  const thankyou = layout === "thankyou";
  const light = variant === "light";

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center gap-[1.5rem] rounded-[0.5rem] border-[0.11rem] border-white/30 px-[2.875rem] pt-[2.875rem] pb-[2.5rem] lg:w-[56.5rem]",
        variant === "dark" ? "bg-[#161616]/[0.19] backdrop-blur-[2rem]" : "bg-[#f9f5ec]",
        monthly && "relative block p-0 lg:h-[21.875rem]",
        monthly && (light ? "max-lg:h-[calc(18.15275*var(--page-rem))]" : "max-lg:h-[calc(19.261616*var(--page-rem))]"),
        thankyou && "gap-[0.7754rem] px-[2.0677rem] py-[1.4646rem] lg:gap-[1.125rem] lg:px-[3rem] lg:py-[2.125rem]",
        className,
      )}>
      {label && (
        <p
          className={cn(
            "mb-0 text-center font-geist text-[2.07rem] font-normal whitespace-nowrap uppercase opacity-70",
            variant === "dark" ? "text-white" : "text-black",
            thankyou && "text-[1.4297rem] leading-[1.3] lg:text-[2.07425rem]",
            monthly &&
              "absolute lg:inset-[2.89125rem_2.89125rem_auto] lg:text-[2.07425rem] lg:leading-[1.3] max-lg:whitespace-normal max-lg:text-[calc(1.75*var(--page-rem))]",
            monthly &&
              (light
                ? "max-lg:inset-[calc(2.39875*var(--page-rem))_calc(2.42*var(--page-rem))_auto] max-lg:leading-[1.3]"
                : "max-lg:inset-[calc(2.545*var(--page-rem))_calc(2.545*var(--page-rem))_auto] max-lg:leading-[1.21]"),
          )}>
          {label}
        </p>
      )}
      <div
        className={cn(
          "w-full border-t",
          light ? "border-black/20" : "border-white/30",
          monthly &&
            "absolute border-0 lg:top-[6.68875rem] lg:left-[2.89125rem] lg:w-[50rem] lg:h-[0.04395rem] after:absolute after:content-[''] after:inset-[-0.125rem_0_auto] after:h-[0.29395rem] after:bg-center after:bg-contain after:bg-no-repeat",
          monthly &&
            (light
              ? "after:bg-[url('/monthly-update/countdown-line-light.svg')] max-lg:top-[calc(5.64125*var(--page-rem))] max-lg:left-[calc(2.48875*var(--page-rem))] max-lg:w-[calc(44.75*var(--page-rem))]"
              : "after:bg-[url('/monthly-update/countdown-line-dark.svg')] max-lg:top-[calc(5.89*var(--page-rem))] max-lg:left-[calc(2.545*var(--page-rem))] max-lg:w-[calc(44.0265*var(--page-rem))]"),
          fitWidth && "max-lg:w-[calc(100%-4.84rem)]",
          monthly &&
            "max-lg:h-[calc(0.03875*var(--page-rem))] max-lg:after:inset-[calc(-0.125*var(--page-rem))_0_auto] max-lg:after:h-[calc(0.29395*var(--page-rem))]",
        )}
      />
      <div
        className={cn(
          "flex items-center gap-[1.5rem] pb-[1rem] text-center lg:gap-[2.75rem]",
          variant === "dark" ? "text-white" : "text-black",
          thankyou && "gap-[1.8954rem] pb-[0.6893rem] lg:pb-[1rem]",
          monthly &&
            "absolute lg:top-[7.825rem] lg:left-[2.89125rem] max-lg:w-[calc(44.75*var(--page-rem))] max-lg:gap-0 max-lg:justify-between max-lg:p-0",
          monthly &&
            (light
              ? "max-lg:top-[calc(6.49375*var(--page-rem))] max-lg:left-[calc(2.40875*var(--page-rem))]"
              : "max-lg:top-[calc(6.89*var(--page-rem))] max-lg:left-[calc(2.545*var(--page-rem))]"),
          fitWidth && "max-lg:w-[calc(100%-4.8175rem)]",
        )}>
        {units.map((unit, i) => (
          <div
            key={unit.label}
            className="contents">
            {i > 0 && (
              <p
                className={cn(
                  "mb-0 font-sans text-[2.6rem] leading-none font-medium opacity-70 lg:text-[2.96rem]",
                  thankyou && "text-[2.0434rem] lg:text-[2.96475rem]",
                  monthly &&
                    "[font-family:'Helvetica_Neue',Helvetica,Arial,sans-serif] lg:w-[0.875rem] lg:text-[2.96475rem]",
                  monthly &&
                    (light
                      ? "max-lg:text-[calc(2.46025*var(--page-rem))]"
                      : "max-lg:text-[calc(2.610625*var(--page-rem))]"),
                )}>
                :
              </p>
            )}
            <div
              className={cn(
                "flex flex-col items-center gap-[0.75rem]",
                thankyou && "gap-[0.5109rem] lg:gap-[0.74125rem]",
                monthly && "lg:gap-[0.74125rem]",
                monthly && (i === 1 ? "lg:w-[8.5rem]" : i === 3 ? "lg:w-[8.625rem]" : "lg:w-[7.875rem]"),
                monthly &&
                  (light
                    ? "max-lg:gap-[calc(0.615125*var(--page-rem))]"
                    : "max-lg:gap-[calc(0.652625*var(--page-rem))]"),
              )}>
              <p
                className={cn(
                  "mb-0 font-geist text-[5.28rem] leading-none font-medium lg:text-[6rem]",
                  thankyou && "text-[4.1354rem] leading-[1.3] lg:text-[6rem]",
                  monthly && "leading-[1.3]",
                  monthly &&
                    (light
                      ? "max-lg:text-[calc(4.979*var(--page-rem))]"
                      : "max-lg:text-[calc(5.283125*var(--page-rem))]"),
                )}>
                {unit.value}
              </p>
              <p
                className={cn(
                  "mb-0 font-geist text-[1.75rem] font-normal uppercase opacity-70 lg:text-[1.815rem]",
                  thankyou && "text-[1.2509rem] leading-[1.3] lg:text-[1.815rem]",
                  monthly && "leading-[1.3]",
                  monthly &&
                    (light
                      ? "max-lg:text-[calc(1.506125*var(--page-rem))]"
                      : "max-lg:text-[calc(1.75*var(--page-rem))]"),
                )}>
                {unit.label}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** CTA title in the current URL language (same resolution as `CTAButton`). */
function useCTATitle(cta?: GROQCTA | null) {
  const language = useUrlLanguage();
  return cta ? resolveI18nText(cta.title, cta.i18n, language) : "";
}

/**
 * Yellow ticket CTA (hero / why-attend / host / final). Takes the same `button`
 * object as `CTAButton`: link, anchor and popup actions come from the referenced
 * `globalCTA` via `CTAWrapper`. Type/size/shape are ignored; the ticket design
 * and tear animation are fixed.
 */
export function TicketCTA({
  data,
  className,
  layout = "event",
  textClassName,
  iconClassName,
}: {
  data?: GROQButton | null;
  className?: string;
  layout?: EventLayout;
  /** Overrides the label typography (replay page uses 18px). */
  textClassName?: string;
  iconClassName?: string;
}) {
  const monthly = layout === "monthly";
  const cta = data?.cta;
  const title = useCTATitle(cta);
  if (!cta || !title) return null;
  return (
    <CTAWrapper
      cta={cta}
      className={cn("group relative flex h-[7.75rem] w-[47.625rem] shrink-0 cursor-pointer items-stretch", className)}>
      {/* Body */}
      <span
        style={CTA_BODY_MASK}
        className="relative flex min-w-0 flex-1 items-center justify-center bg-[#ffda74] px-[3.8rem] transition-transform duration-300 ease-out group-hover:-translate-x-[0.375rem]">
        <span
          className={cn(
            "text-center font-geist text-[2rem] leading-[2.7rem] font-medium whitespace-nowrap text-black",
            monthly && "max-lg:text-[calc(2*var(--page-rem))] max-lg:leading-[1.35]",
            textClassName,
          )}>
          {title}
        </span>
      </span>
      {/* Stub */}
      <span
        style={CTA_STUB_MASK}
        className={cn(
          "relative flex w-[8.4375rem] shrink-0 origin-left items-center justify-center bg-[#ffda74] transition-transform duration-300 ease-out group-hover:translate-x-[0.75rem] group-hover:rotate-[6deg]",
          monthly && "max-lg:w-[calc(8.5*var(--page-rem))]",
        )}>
        <NextImage
          src={logoMark}
          alt=""
          aria-hidden
          className={cn("h-[2.58rem] w-[2.58rem]", iconClassName)}
        />
      </span>
    </CTAWrapper>
  );
}

/** White navbar ticket. `text` overrides the CTA title (mobile variant). */
export function NavTicketButton({
  data,
  text,
  className,
  layout = "event",
}: {
  data?: GROQButton | null;
  text?: string;
  className?: string;
  layout?: EventLayout;
}) {
  const monthly = layout === "monthly";
  const cta = data?.cta;
  const ctaTitle = useCTATitle(cta);
  const title = text || ctaTitle;
  if (!cta || !title) return null;
  return (
    <CTAWrapper
      cta={cta}
      className={cn(
        "group relative flex h-[5.75rem] w-auto shrink-0 cursor-pointer items-stretch",
        monthly && "max-lg:h-[calc(5.75*var(--page-rem))]",
        className,
      )}>
      {/* Body */}
      <span
        style={NAV_BODY_MASK}
        className={cn(
          "relative flex items-center bg-white px-[2.25rem] transition-transform duration-300 ease-out group-hover:-translate-x-[0.25rem]",
          monthly && "max-lg:px-[calc(2.228125*var(--page-rem))]",
        )}>
        <span
          className={cn(
            "text-center font-geist text-[1.75rem] leading-[2rem] font-medium whitespace-nowrap text-black",
            monthly && "max-lg:text-[calc(1.75*var(--page-rem))]",
          )}>
          {title}
        </span>
        {/* Faint inset frame on the body only */}
        <span
          aria-hidden
          className="pointer-events-none absolute top-[0.5rem] right-[1.03rem] bottom-[0.5rem] left-[0.33rem] rounded-[0.19rem] border-[0.09rem] border-white/[0.28]"
        />
      </span>
      {/* Stub */}
      <span
        style={NAV_STUB_MASK}
        className={cn(
          "relative flex w-[7.125rem] shrink-0 origin-left items-center justify-center bg-white transition-transform duration-300 ease-out group-hover:translate-x-[0.5rem] group-hover:rotate-[6deg]",
          monthly && "max-lg:w-[calc(6.75*var(--page-rem))]",
        )}>
        <NextImage
          src={logoMark}
          alt=""
          aria-hidden
          className={cn(
            "h-[2.46rem] w-[2.58rem]",
            monthly && "max-lg:w-[calc(2.5785*var(--page-rem))] max-lg:h-auto max-lg:max-w-none max-lg:object-contain",
          )}
        />
      </span>
    </CTAWrapper>
  );
}

/** Plain black rectangular CTA (thank-you WhatsApp banner). Same `button` data as `TicketCTA`. */
export function BlackCTA({ data, className }: { data?: GROQButton | null; className?: string }) {
  const cta = data?.cta;
  const title = useCTATitle(cta);
  if (!cta || !title) return null;
  return (
    <CTAWrapper
      cta={cta}
      className={cn(
        "flex cursor-pointer items-center justify-center rounded-[0.25rem] bg-black px-[3.5rem] py-[2.25rem] transition-opacity hover:opacity-85",
        className,
      )}>
      <span className="font-geist text-[1.75rem] leading-[1.35] font-semibold tracking-[-0.035rem] text-white capitalize">
        {title}
      </span>
    </CTAWrapper>
  );
}

/** Dark hero backdrop: dotted grid, glowing chart line, two candle clusters. */
export function EventHeroBackdrop({
  candlePositions = ["left-[-3.5rem] top-[50.875rem]", "right-[-8.875rem] top-[7.25rem]"],
  wavePosition = "top-[56.625rem] left-[-7.75rem]",
}: {
  candlePositions?: [string, string];
  wavePosition?: string;
}) {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.23]"
        style={{
          backgroundImage: `url(${gridTile.src})`,
          backgroundSize: "3.5rem 3.5rem",
          backgroundPosition: "top left",
        }}
      />
      <div
        aria-hidden
        className={cn("pointer-events-none absolute h-[156.25rem] w-[271.875rem]", wavePosition)}>
        <div className="absolute inset-[-47.12%_-27.07%]">
          <NextImage
            src={wave}
            alt=""
            className="block !h-full !w-full !max-w-none"
          />
        </div>
      </div>
      {candlePositions.map((pos) => (
        <div
          key={pos}
          aria-hidden
          className={cn("pointer-events-none absolute h-[96.75rem] w-[109rem] overflow-clip opacity-[0.13]", pos)}>
          <div className="absolute inset-[73.54%_156.63%_-0.03%_-108.49%] opacity-60 mix-blend-overlay">
            <NextImage
              src={candle1}
              alt=""
              className="absolute inset-0 !h-full !w-full !max-w-none"
            />
          </div>
          <div className="absolute inset-[49.19%_104.49%_24.32%_-56.34%] opacity-60 mix-blend-overlay">
            <NextImage
              src={candle2}
              alt=""
              className="absolute inset-0 !h-full !w-full !max-w-none"
            />
          </div>
          <div className="absolute inset-[24.35%_52.15%_49.15%_-4%] opacity-60 mix-blend-overlay">
            <NextImage
              src={candle3}
              alt=""
              className="absolute inset-0 !h-full !w-full !max-w-none"
            />
          </div>
          <div className="absolute inset-[0_0_73.51%_48.15%] opacity-60 mix-blend-overlay">
            <NextImage
              src={candle4}
              alt=""
              className="absolute inset-0 !h-full !w-full !max-w-none"
            />
          </div>
        </div>
      ))}
    </>
  );
}

/** Event navbar: glass bar with wordmark + white ticket button (desktop/mobile text variants). */
export function EventNavbar({
  data,
  textMobile,
  countdownTarget,
  language,
  layout = "event",
}: {
  data?: GROQButton | null;
  textMobile?: string;
  /** When set, renders the compact "ends in" countdown under the bar on mobile. */
  countdownTarget?: string;
  language?: string;
  layout?: EventLayout;
}) {
  const monthly = layout === "monthly";
  return (
    <header
      className={cn(
        "absolute top-[1.75rem] left-1/2 z-20 flex w-[50rem] -translate-x-1/2 flex-col gap-[2.125rem] lg:w-[150rem]",
        monthly &&
          "max-lg:top-[calc(1.75*var(--page-rem))] max-lg:w-[min(calc(50*var(--page-rem)),calc(100%-3.75*var(--page-rem)))]",
      )}>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        className={cn(
          "flex w-full items-center justify-between rounded-[0.5rem] border border-white/10 bg-white/[0.04] py-[0.75rem] pr-[0.75rem] pl-[2.75rem] backdrop-blur-[0.25rem] lg:pr-[1.5rem]",
          monthly &&
            "max-lg:h-[calc(7.25*var(--page-rem))] max-lg:p-[calc(0.75*var(--page-rem))] max-lg:pl-[calc(2.75*var(--page-rem))]",
        )}>
        <NextImage
          src={wordmark}
          alt="IQ Capital"
          className={cn(
            "h-[2.6875rem] w-[14.5rem] shrink-0",
            monthly &&
              "max-lg:w-[calc(14.5*var(--page-rem))] max-lg:h-[calc(2.6855*var(--page-rem))] max-lg:max-w-none max-lg:object-contain",
          )}
        />
        <NavTicketButton
          data={data}
          layout={layout}
          className="hidden lg:flex"
        />
        <NavTicketButton
          data={data}
          layout={layout}
          text={textMobile}
          className="lg:hidden"
        />
      </motion.div>
      {countdownTarget !== undefined && (
        <div className="lg:hidden">
          <NavCountdown
            target={countdownTarget}
            language={language}
          />
        </div>
      )}
    </header>
  );
}

export interface EventFooterLink {
  _key?: string;
  label?: string;
  url?: string;
}

/**
 * Footer bar: copyright, centered wordmark, slash-separated links.
 * `dark` (event/thank-you) is the glass bar pinned to the section bottom;
 * `light` (replay) is a static white bar with a top rule and black text.
 */
export function EventFooter({
  copyright,
  links = [],
  variant = "dark",
  layout = "event",
  className,
  linksClassName,
}: {
  copyright?: string;
  links?: EventFooterLink[];
  variant?: "dark" | "light";
  layout?: EventLayout;
  className?: string;
  linksClassName?: string;
}) {
  const light = variant === "light";
  const monthly = layout === "monthly";
  return (
    <footer
      className={cn(
        "w-full px-[2rem]",
        light
          ? "relative border-t border-black/10 pt-[3rem] pb-[2.2rem] lg:border-black/20 lg:py-[3.5rem]"
          : "absolute bottom-0 left-0 z-10 bg-black/[0.12] py-[2rem] backdrop-blur-[1rem]",
        monthly &&
          "max-lg:flex max-lg:items-center max-lg:h-[calc(18.25*var(--page-rem))] max-lg:p-[calc(2*var(--page-rem))]",
        className,
      )}>
      <div
        className={cn(
          "mx-auto flex w-full flex-col items-center lg:w-[140rem] lg:flex-row lg:items-center lg:justify-between lg:gap-0",
          light ? "gap-[1.5rem]" : "gap-[2.5rem]",
          monthly && "max-lg:gap-[calc(2.5*var(--page-rem))]",
        )}>
        <NextImage
          src={wordmark}
          alt="IQ Capital"
          className={cn(
            "order-1 lg:order-2 lg:h-[3.6rem] lg:w-[19.375rem]",
            light ? "h-[3.59rem] w-[19.375rem] brightness-0" : "h-[3.15rem] w-[17rem]",
            monthly && "max-lg:w-[calc(17*var(--page-rem))] max-lg:h-[calc(3.148625*var(--page-rem))]",
          )}
        />
        {copyright && (
          <p
            className={cn(
              "order-2 mb-0 font-geist text-[2rem] leading-[3rem] font-normal lg:order-1 lg:w-[45.125rem]",
              light ? "text-black opacity-[0.79]" : "text-white opacity-80",
              monthly && "max-lg:text-[calc(2*var(--page-rem))] max-lg:leading-[calc(3*var(--page-rem))]",
            )}>
            {copyright}
          </p>
        )}
        {!!links.length && (
          <p
            className={cn(
              "order-3 mb-0 max-w-full text-center font-geist text-[2rem] leading-[3rem] font-normal lg:text-right lg:whitespace-nowrap",
              light ? "text-black opacity-[0.79]" : "text-white opacity-80",
              monthly && "max-lg:text-[calc(2*var(--page-rem))] max-lg:leading-[calc(3*var(--page-rem))]",
              linksClassName,
            )}>
            {links.map((link, i) => (
              <span key={link._key ?? i}>
                {i > 0 && " / "}
                {link.url ? (
                  <a
                    href={link.url}
                    className="hover:underline">
                    {link.label}
                  </a>
                ) : (
                  link.label
                )}
              </span>
            ))}
          </p>
        )}
      </div>
    </footer>
  );
}
